import { BarChart3 as BarChart, CreditCard, Database, DollarSign, FileText, Network, TrendingUp, Users } from "lucide-react";
import React from "react";
import { cn } from "src/common/utils";
import { Button } from "src/components/ui/button";
import { useMe, usePermissions } from "src/context/MeContext";
import { isRootlyBranded } from "src/utils/brandTheme";

import { SettingsSection,useSettings } from "../context/SettingsContext";

interface SettingsSidebarProps {
  showAdminSettings: boolean;
  isSuperAdmin?: boolean;
}

export default function SettingsSidebar({ showAdminSettings, isSuperAdmin = false }: SettingsSidebarProps) {
  const { activeSection, setActiveSection } = useSettings();
  const { canRead } = usePermissions();
  const me = useMe();
  const brandTheme = me.organization?.brandTheme ?? "TIERZERO";
  // ROOTLY and ROOTLY_COBRAND orgs get Rootly-specific UI changes (not ROOTLY_HYBRID)
  const isPureRootly = brandTheme === "ROOTLY" || brandTheme === "ROOTLY_COBRAND";

  // Build manage items based on user scopes
  const manageItems = [
    ...(canRead("integrations") ? [{
      id: "integrations" as SettingsSection,
      label: "Integrations",
      icon: <Database className="size-4" />
    }] : []),
    ...(canRead("teams") ? [{
      id: "teams" as SettingsSection,
      label: "Teams",
      icon: <Users className="size-4" />
    }] : []),
    ...(canRead("integrations") ? [{
      id: "network" as SettingsSection,
      label: "Network",
      icon: <Network className="size-4" />
    }] : []),
    ...(!isPureRootly && canRead("billing") ? [{
      id: "usage" as SettingsSection,
      label: "Usage",
      icon: <BarChart className="size-4" />
    }] : []),
    ...(!isPureRootly && canRead("billing") ? [{
      id: "cost" as SettingsSection,
      label: "Cost",
      icon: <DollarSign className="size-4" />
    }] : []),
    ...(!isRootlyBranded(brandTheme) && canRead("billing") ? [{
      id: "billing" as SettingsSection,
      label: "Billing",
      icon: <CreditCard className="size-4" />
    }] : []),
  ];

  // Build admin items based on user scopes
  const adminItems = [
    ...(!isPureRootly && isSuperAdmin ? [{
      id: "value" as SettingsSection,
      label: "Value",
      icon: <TrendingUp className="size-4" />
    }] : []),
    ...(showAdminSettings ? [{
      id: "audit-logs" as SettingsSection,
      label: "Audit Logs",
      icon: <FileText className="size-4" />
    }] : []),
  ];

  const renderNavItems = (items: typeof manageItems) => (
    items.map((item) => (
      <Button
        key={item.id}
        variant="ghost"
        className={cn(
          "w-full justify-start gap-2 px-3 h-8 text-[13px] font-medium rounded-md transition-colors",
          activeSection === item.id
            ? "bg-accent text-foreground hover:bg-accent hover:text-foreground"
            : "text-muted-foreground hover:bg-accent hover:text-foreground"
        )}
        asChild
      >
        <a
          href={`/settings/${item.id}`}
          onClick={(e) => {
            e.preventDefault();
            setActiveSection(item.id);
          }}
        >
          {item.icon}
          {item.label}
        </a>
      </Button>
    ))
  );

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <nav className="space-y-1">
          <div className="pt-3 pb-1 text-xs text-muted-foreground uppercase tracking-wider">
            SETTINGS
          </div>
          <div className="space-y-1.5">
            {renderNavItems(manageItems)}
          </div>

          {adminItems.length > 0 && (
            <>
              <div className="pt-3 pb-1 text-xs text-muted-foreground uppercase tracking-wider">
                INTERNAL ADMIN
              </div>
              <div className="space-y-1.5">
                {renderNavItems(adminItems)}
              </div>
            </>
          )}
        </nav>
      </div>
    </div>
  );
}
